// Pure composer for the custom-order spec sub-line shown under a "Custom Order
// Request" line in the cart and on the invoice. No I/O so it is unit-tested
// directly (mirrors lib/invoices.ts composeLineSpec). Shared by the cart and the
// checkout route so the two cannot drift into different house styles.

export interface CustomOrderSpec {
  /** Reference catalog piece the customer chose, if any. */
  referencePiece?: string | null;
  /** Ring | Necklace | Earrings | Bracelet. */
  pieceType?: string | null;
  /** Yellow/White/Rose Gold. */
  goldColor?: string | null;
  /** Chosen stone by name (stone mode), e.g. "Diamond". */
  stone?: string | null;
  /** Chosen stone by colour (colour mode), e.g. "Blue". */
  stoneColor?: string | null;
  /** Ring size (bare number) or necklace/bracelet length (carries its own unit). */
  size?: string | null;
}

const clean = (v: string | null | undefined): string => (v ?? "").trim();

/**
 * Compose a compact one-line spec, joining the present fields with " · ":
 *   "Ring · Yellow Gold · Diamond · Size 7"
 * A colour-mode stone renders as "Blue stone". A bare-number ring size renders as
 * "Size 7"; a length ('16"') already carries its unit and is shown as-is. A
 * reference piece is appended as "Ref: <name>". Returns "" when nothing is set.
 */
export function composeCustomOrderSpec(spec: CustomOrderSpec): string {
  const pieceType = clean(spec.pieceType);
  const goldColor = clean(spec.goldColor);
  const stone = clean(spec.stone);
  const stoneColor = clean(spec.stoneColor);
  const size = clean(spec.size);
  const reference = clean(spec.referencePiece);

  const stoneLabel = stone || (stoneColor ? `${stoneColor} stone` : "");
  const sizeLabel = size ? (/^\d+(\.\d+)?$/.test(size) ? `Size ${size}` : size) : "";
  const referenceLabel = reference ? `Ref: ${reference}` : "";

  return [pieceType, goldColor, stoneLabel, sizeLabel, referenceLabel]
    .filter(Boolean)
    .join(" · ");
}
